import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { gbp } from "./crmPipelineShared";
import { DateField, localToday } from "./DateField";
import { LoadError } from "./LoadError";

const monthStart = () => localToday().slice(0, 8) + "01";
const shift = (months) => { const d = new Date(); d.setMonth(d.getMonth() - months); d.setDate(1); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const PRESETS = [["This month", () => [monthStart(), localToday()]], ["Last 3 months", () => [shift(2), localToday()]],
  ["This year", () => [localToday().slice(0, 4) + "-01-01", localToday()]], ["All time", () => ["", ""]]];
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

function Tile({ label, value, note, testId, tone = "text-white" }) {
  return (
    <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-4" data-testid={testId}>
      <div className={`text-2xl font-mono ${tone}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-emerald-100/60 mt-1">{label}</div>
      {note && <div className="text-[10px] text-emerald-100/40 mt-0.5">{note}</div>}
    </div>
  );
}

function SourceTable({ rows }) {
  const shown = rows.filter((r) => r.enquiries > 0);
  return (
    <div className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-x-auto" data-testid="source-table">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase text-emerald-100/50 border-b border-white/10">
          {["Original source", "Enquiries", "Qualified", "Referred", "Instructed", "Completed", "Commission received", "Referral rate"].map((h) => <th key={h} className="py-2.5 px-3 font-medium">{h}</th>)}
        </tr></thead>
        <tbody>
          {shown.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-emerald-100/40">No enquiries in this period.</td></tr>}
          {shown.map((r) => (
            <tr key={r.source} className="border-b border-white/5" data-testid={`source-row-${r.source.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <td className="py-2.5 px-3 text-white">{r.source}</td>
              {["enquiries", "qualified", "referred", "instructed", "completed"].map((k) => <td key={k} className="px-3 font-mono text-emerald-100/80">{r[k]}</td>)}
              <td className="px-3 font-mono text-emerald-300">{gbp(r.commission_received)}</td>
              <td className="px-3 font-mono text-brass">{pct(r.referral_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-emerald-100/40 px-3 py-2">Grouped by the lead's original source, never overwritten as it progresses. Ad spend isn't tracked, so cost per lead isn't shown.</p>
    </div>
  );
}

export default function CrmReferralDashboard() {
  const [[from, to], setRange] = useState(PRESETS[0][1]());
  const [preset, setPreset] = useState("This month");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(async () => {
    setErr(false);
    try { const { data } = await api.get("/crm/referral-dashboard", { params: { date_from: from, date_to: to } }); setD(data); }
    catch (e) { setErr(true); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);
  const c = d?.counts || {}; const r = d?.rates || {}; const m = d?.commission || {};

  return (
    <CrmShell title="Referral Dashboard">
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="dash-range">
        {PRESETS.map(([l, fn]) => (
          <button key={l} onClick={() => { setPreset(l); setRange(fn()); }} data-testid={`dash-preset-${l.replace(/\s/g, "-").toLowerCase()}`}
            className={`text-xs rounded-full px-3 py-1.5 ${preset === l ? "bg-brass text-emerald-950 font-semibold" : "bg-white/5 text-emerald-100/70 hover:bg-white/10"}`}>{l}</button>
        ))}
        <div className="w-40"><DateField value={from} onChange={(v) => { setPreset("Custom"); setRange([v, to]); }} testId="dash-from" placeholder="From" /></div>
        <div className="w-40"><DateField value={to} onChange={(v) => { setPreset("Custom"); setRange([from, v]); }} testId="dash-to" placeholder="To" /></div>
      </div>
      {err && <LoadError message="Dashboard could not be loaded." onRetry={load} testId="dash-error" />}
      {!d && !err && <p className="text-sm text-emerald-100/50">Loading…</p>}
      {d && (
        <div className="space-y-6">
          {d.needs_review > 0 && (
            <Link to="/crm/stage-review" data-testid="dash-review-link" className="flex items-center gap-2 bg-amber-500/10 border border-amber-400/30 rounded-xl px-4 py-3 text-sm text-amber-200">
              <AlertTriangle className="w-4 h-4" /> {d.needs_review} migrated lead(s) need a stage review. They're left out of the qualified, lost and purchase counts until you review them.
            </Link>
          )}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2">Vendor enquiries <span className="text-emerald-100/40 font-normal text-xs">— by date the enquiry was created</span></h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Tile label="Vendor enquiries" value={c.vendor_enquiries} testId="dash-enquiries" note="duplicates by email/phone merged" />
              <Tile label="Still new" value={c.new_enquiries} testId="dash-new" />
              <Tile label="Qualified" value={c.qualified} testId="dash-qualified" />
              <Tile label="Active now" value={c.active_opportunities} testId="dash-active" note="current, ignores the date range" />
              <Tile label="Lost / withdrawn" value={c.lost_or_withdrawn} testId="dash-lost" tone="text-red-300" />
              <Tile label="Bought by Emerald Crown" value={c.ec_purchases} testId="dash-ec" note="own purchases, not referrals" />
            </div>
          </section>
          <section>
            <h2 className="text-sm font-semibold text-white mb-2">Auction referral milestones <span className="text-emerald-100/40 font-normal text-xs">— by the date each milestone happened</span></h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Tile label="Referrals made" value={c.referrals_made} testId="dash-referrals" note={`${c.unique_vendors_referred} vendor(s) · CRM referral conversions, not sales`} tone="text-violet-300" />
              <Tile label="Instructions signed" value={c.instructions_signed} testId="dash-instructions" />
              <Tile label="Listed" value={c.listed} testId="dash-listed" />
              <Tile label="Sold (awaiting completion)" value={c.sold} testId="dash-sold" />
              <Tile label="Sales completed" value={c.sales_completed} testId="dash-completed" tone="text-emerald-300" />
              <Tile label="Commissions paid" value={c.commissions_received} testId="dash-paid" />
            </div>
          </section>
          <section>
            <h2 className="text-sm font-semibold text-white mb-2">Conversion rates</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Tile label="Enquiry → referral" value={pct(r.enquiry_to_referral)} testId="rate-enquiry-referral" note="enquiries created in range that were referred (each vendor once)" tone="text-brass" />
              <Tile label="Qualified → referral" value={pct(r.qualified_to_referral)} testId="rate-qualified-referral" note="qualified enquiries created in range" tone="text-brass" />
              <Tile label="Referral → instruction" value={pct(r.referral_to_instruction)} testId="rate-referral-instruction" note="referrals made in range" tone="text-brass" />
              <Tile label="Instruction → completion" value={pct(r.instruction_to_completion)} testId="rate-instruction-completion" note="instructions signed in range" tone="text-brass" />
            </div>
          </section>
          <section>
            <h2 className="text-sm font-semibold text-white mb-2">Commission <span className="text-emerald-100/40 font-normal text-xs">— forecasts are kept apart from actual income</span></h2>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <Tile label="Potential (forecast)" value={gbp(m.potential)} testId="money-potential" note="referred, not yet instructed · now" />
              <Tile label="Expected from instructed (forecast)" value={gbp(m.expected_instructed)} testId="money-expected" note="instructed, sale not completed · now" />
              <Tile label="Earned in range" value={gbp(m.earned)} testId="money-earned" note="due once a sale completes" />
              <Tile label="Earned, awaiting payment" value={gbp(m.earned_unpaid)} testId="money-unpaid" note="now" />
              <Tile label="Received (actual income)" value={gbp(m.received)} testId="money-received" tone="text-emerald-300" note="paid in range" />
            </div>
            <p className="text-[10px] text-emerald-100/40 mt-1.5">Invoicing isn't tracked separately yet. Expected commission is 1% of the sale price, or the asking price until there's a sale, unless overridden.</p>
          </section>
          <section>
            <h2 className="text-sm font-semibold text-white mb-2">Performance by lead source</h2>
            <SourceTable rows={d.sources} />
          </section>
        </div>
      )}
    </CrmShell>
  );
}
